import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useNavigate } from "react-router-dom";

type User = {
  email: string;
  nome: string;
  roles: string[];
  impersonating?: boolean;
  imageUrl?: string;
  originalUser?: {
    email: string;
    nome: string;
    roles: string[];
    imageUrl?: string;
  };
  empresas?: {
    id: string;
    codigo: number;
    nomeFantasia: string;
    localDeTrabalho: boolean;
    serieNFe?: number;
    homologacao: boolean;
  }[];
};

interface Usuario {
  email: string;
  nome: string;
  roles: string[];
}

export const useUser = () => {
  const navigate = useNavigate();
  const query = useQuery<User>({
    queryKey: ["account", "user"],
    queryFn: async () => {
      const response = await axios.get("/api/account/user");
      return response.data;
    },
    retry: (failureCount, error) => {
      const accessDenied = [401, 403];
      if (axios.isAxiosError(error) && accessDenied.includes(error.response?.status ?? 0)) {
        return false; // não faz retry se for 401
      }
      return true; // faz retry para outros erros
    },
  });

  const isAccessDenied = axios.isAxiosError(query.error) && query.error?.response?.status === 403;

  if (isAccessDenied) {
    navigate("/pages/errors/403");
  }

  return {
    user: query.data,
    ...query,
  };
};

export const useLogout = () => {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async () => {
      return await axios.post("/api/account/logout");
    },
    onSuccess: async () => {
      //redirect to login page
      navigate("/pages/authentication/simple/sign-in");
    },
  });
};

export const useLogin = () => {
  const { refetch } = useUser();
  return useMutation<
    {
      data: {
        sucesso: number;
        usuario: {
          email: string;
          nome: string;
          roles: string[];
          imageUrl: string;
        };
      };
    },
    any,
    { email: string; senha: string; lembrarMe: boolean }
  >({
    mutationFn: async (data) => {
      return await axios.post("/api/account/login", data);
    },
    onSuccess: async (data) => {
      await refetch();
    },
  });
};

export const useImpersonate = () => {
  const navigate = useNavigate();
  const { refetch } = useUser();
  return useMutation({
    mutationFn: async (email: string) => {
      return await axios.post("/api/account/impersonate", { email });
    },
    onSuccess: () => {
      refetch();
      navigate("/");
    },
  });
};

export const useUsers = () => {
  return useQuery<Usuario[]>({
    queryKey: ["users"],
    queryFn: async () => {
      const response = await axios.get("/api/account/users");
      return response.data;
    },
  });
};

export const useStopImpersonate = () => {
  const navigate = useNavigate();
  const { refetch } = useUser();
  return useMutation({
    mutationFn: async () => {
      return await axios.post("/api/account/stop-impersonation");
    },
    onSuccess: () => {
      refetch();
      navigate("/");
    },
  });
};
